import React, { useState, useEffect } from "react";
import PodsService from "../services/podsService";
import { useSearchParams } from "react-router-dom";

type Pod = {
    id: string;
    addressTraceId: string;
    podUrl: string;
    createdAt: string;
    // Add other fields if returned by API
};

const Pods: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [traceId, setTraceId] = useState<string>(searchParams.get("traceId") || "");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pods, setPods] = useState<Pod[]>([]);
    const [selectedPod, setSelectedPod] = useState<Pod | null>(null);

    // Effect to perform search if traceId is present in URL on mount
    useEffect(() => {
        const urlTraceId = searchParams.get("traceId");
        if (urlTraceId) {
            setTraceId(urlTraceId);
            performSearch(urlTraceId);
        }
    }, [searchParams]);

    const performSearch = async (searchTraceId: string) => {
        if (!searchTraceId.trim()) return;

        setLoading(true);
        setError(null);
        setPods([]); // Clear previous results

        try {
            const response = await PodsService.getPODsByTraceId(searchTraceId.trim());
            // Assuming response.data is an array of PODs. Adjust based on actual API response structure.
            const data = response.data;

            if (Array.isArray(data) && data.length > 0) {
                // Sort by newest first if not already sorted
                // Assuming createdAt is an ISO string
                const sortedData = data.sort((a: Pod, b: Pod) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                setPods(sortedData);
            } else if (Array.isArray(data) && data.length === 0) {
                setError("No PODs found");
            } else {
                // Fallback if data is not an array for some reason
                setPods([]);
                setError("Unexpected response format");
            }

        } catch (err: any) {
            console.error("Error fetching PODs:", err);
            if (err.response && err.response.status === 404) {
                setError("No PODs found");
            } else if (err.response && err.response.status === 400) {
                setError("Invalid trace ID");
            } else {
                setError("An error occurred while fetching PODs.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (traceId) {
            setSearchParams({ traceId });
            performSearch(traceId);
        }
    };

    return (
        <div className="main-content">
            <div className="container-fluid px-1 py-1">
                <div className="row mb-1">
                    <div className="col">
                        <h2 className="fw-bold mb-1">PODs</h2>
                        <p className="text-muted">Search for Proof of Delivery images by Trace ID</p>
                    </div>
                </div>

                {/* Search Interface */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                        <form onSubmit={handleSearch} className="row g-3">
                            <div className="col-md-12">
                                <label htmlFor="traceId" className="form-label fw-medium mb-1">Search by Trace ID</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        id="traceId"
                                        placeholder="Enter Trace ID"
                                        value={traceId}
                                        onChange={(e) => setTraceId(e.target.value)}
                                        disabled={loading}
                                    />
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={loading || !traceId.trim()}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Searching...
                                            </>
                                        ) : (
                                            "Search"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger border-0 shadow-sm d-flex align-items-center mb-3" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                        <div>{error}</div>
                    </div>
                )}

                {/* Results Grid */}
                {pods.length > 0 && (
                    <div className="row g-3">
                        {pods.map((pod) => (
                            <div key={pod.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                                <div
                                    className="card h-100 border-0 shadow-sm pod-card"
                                    onClick={() => setSelectedPod(pod)}
                                    style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                                >
                                    <div style={{ paddingBottom: '75%', position: 'relative', overflow: 'hidden', borderRadius: '4px' }}>
                                        <img
                                            src={pod.podUrl}
                                            className="card-img-top position-absolute w-100 h-100"
                                            alt={`POD ${pod.id}`}
                                            style={{ objectFit: 'cover', top: 0, left: 0 }}
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                                            }}
                                        />
                                    </div>
                                    <div className="card-body p-2">
                                        <small className="text-muted d-block mb-1">ID: {pod.id}</small>
                                        <small className="text-primary fw-bold">
                                            {new Date(pod.createdAt).toLocaleString()}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Empty State (if searched but no results, handled by error usually, but could be specific UI) */}
                {!loading && !error && pods.length === 0 && traceId && (
                    <div className="text-center py-5">
                        {/* Optional: Add an empty state illustration if desired, but error state covers "No PODs found" */}
                    </div>
                )}

                {/* Image Preview Modal */}
                {selectedPod && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1} role="dialog" onClick={() => setSelectedPod(null)}>
                        <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content border-0 shadow-lg">
                                <div className="modal-header border-bottom-0">
                                    <h5 className="modal-title">POD Details</h5>
                                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedPod(null)}></button>
                                </div>
                                <div className="modal-body text-center bg-light p-0 d-flex justify-content-center align-items-center" style={{ minHeight: '400px', backgroundColor: '#f8f9fa' }}>
                                    <img
                                        src={selectedPod.podUrl}
                                        alt={`POD ${selectedPod.id}`}
                                        className="img-fluid"
                                        style={{ maxHeight: '70vh' }}
                                    />
                                </div>
                                <div className="modal-footer justify-content-between">
                                    <div className="text-start">
                                        <div><strong>ID:</strong> {selectedPod.id}</div>
                                        <div><strong>Created:</strong> {new Date(selectedPod.createdAt).toLocaleString()}</div>
                                        <div className="text-muted small">Trace: {selectedPod.addressTraceId}</div>
                                    </div>
                                    <div>
                                        <a
                                            href={selectedPod.podUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-outline-primary me-2"
                                        >
                                            <i className="bi bi-box-arrow-up-right me-2"></i>
                                            Open Original
                                        </a>
                                        <button type="button" className="btn btn-secondary" onClick={() => setSelectedPod(null)}>Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <style>{`
        .pod-card:hover {
            transform: translateY(-2px);
        }
      `}</style>
        </div>
    );
};

export default Pods;
